import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Store } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useUpdateStore } from '@/features/stores';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import {
  fromReturnsSettings,
  returnsSettingsSchema,
  toReturnsSettingsPatch,
  type ReturnsSettingsForm,
} from '../schemas/returns-settings-schema';

const mapField = (field: string) => field.replace(/^settings\.returns\./, '');
const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'windowDays',
  'allowedByDefault',
  'restockingFeePercent',
  'returnShippingPaidBy',
]);

/**
 * `settings.returns` of the store: the shopper's window, the default fee and who pays shipping.
 * Read-only without `settings.write` and on a closed store.
 */
export function ReturnsSettingsCard({ store }: { store: Store }) {
  const { t } = useTranslation(['returns', 'common']);
  const readOnly = !useStoreEditable({ store, permission: 'settings.write' }).editable;
  const update = useUpdateStore(store.id, 'returns:toasts.settingsSaved');
  const message = useErrorMessage();
  const form = useForm<ReturnsSettingsForm>({
    resolver: useI18nResolver(returnsSettingsSchema),
    defaultValues: fromReturnsSettings(store),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync(toReturnsSettingsPatch(values));
      form.reset(values);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });
  useSaveBar({ form, onSave: submit, saving: update.isPending, disabled: readOnly });

  return (
    <FormSection
      id={SECTION_ANCHORS.returnPolicy}
      title={t('returns:settings.title')}
      description={t('returns:settings.description')}
    >
      <Form {...form}>
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="windowDays"
              render={({ field }) => (
                <FormItem data-tour="settings.returns.window">
                  <FormLabel>{t('returns:settings.windowDays')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={365}
                      disabled={readOnly}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('returns:settings.windowDaysHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="restockingFeePercent"
              render={({ field }) => (
                <FormItem data-tour="settings.returns.fee">
                  <FormLabel>{t('returns:settings.feePercent')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={100}
                      disabled={readOnly}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('returns:settings.feePercentHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="returnShippingPaidBy"
            render={({ field }) => (
              <FormItem data-tour="settings.returns.paid-by">
                <FormLabel>{t('returns:settings.paidBy')}</FormLabel>
                <Select value={field.value} onValueChange={field.onChange} disabled={readOnly}>
                  <FormControl>
                    <SelectTrigger className="w-full sm:w-64">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="customer">{t('returns:paidBy.customer')}</SelectItem>
                    <SelectItem value="store">{t('returns:paidBy.store')}</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>{t('returns:settings.paidByHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="allowedByDefault"
            render={({ field }) => (
              <FormItem
                className="flex flex-row items-center justify-between gap-4 rounded-md border p-3"
                data-tour="settings.returns.default-returnable"
              >
                <div className="grid gap-1">
                  <FormLabel>{t('returns:settings.allowedByDefault')}</FormLabel>
                  <FormDescription>{t('returns:settings.allowedByDefaultHint')}</FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={readOnly}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          {readOnly ? null : (
            <div>
              <Button
                type="submit"
                disabled={update.isPending || !form.formState.isDirty}
                data-tour="settings.returns.save"
              >
                {update.isPending ? t('common:actions.saving') : t('returns:settings.submit')}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </FormSection>
  );
}
